import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Mail, ShieldCheck, AlertTriangle, ArrowRight, CheckCircle2 } from "lucide-react";
import { Link } from "react-router-dom";

function fmtPct(n) { return n == null ? "—" : `${Number(n).toFixed(1)}%`; }
function fmtTs(t) { return t ? new Date(t).toLocaleString() : "never"; }

/** Compact email health widget for the Executive Dashboard.
 * Admin-only visibility (silently no-ops if the caller lacks role). */
export default function EmailHealthWidget() {
  const [data, setData] = useState(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    let cancelled = false;
    api.get("/admin/email/health")
      .then(({ data }) => { if (!cancelled) setData(data); })
      .catch(() => { if (!cancelled) setError(true); });
    return () => { cancelled = true; };
  }, []);

  if (error || !data) return null;

  const domainTone = data.domain_status_label === "verified" ? "#166534"
    : data.domain_status_label === "partial" ? "#92400E" : "#991B1B";
  const domainBg = data.domain_status_label === "verified" ? "#DCFCE7"
    : data.domain_status_label === "partial" ? "#FEF3C7" : "#FEE2E2";
  const successTone = data.delivery_success_pct >= 95 ? "#166534"
    : data.delivery_success_pct >= 80 ? "#92400E" : "#991B1B";

  return (
    <div
      data-testid="email-health-widget"
      style={{
        background: "linear-gradient(135deg, #1E3A8A 0%, #0F172A 100%)",
        color: "#fff",
        borderRadius: 16,
        padding: "18px 22px",
        display: "flex",
        flexDirection: "column",
        gap: 14,
        minHeight: 200,
        boxShadow: "0 10px 24px rgba(15,23,42,0.24)",
      }}
    >
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "start" }}>
        <div>
          <div style={{
            fontSize: 10, letterSpacing: "0.2em", textTransform: "uppercase",
            color: "#F59E0B", fontWeight: 700, marginBottom: 4,
          }}>
            Email Infrastructure
          </div>
          <div style={{ fontSize: 18, fontWeight: 700, display: "flex", alignItems: "center", gap: 8 }}>
            <Mail size={18} /> {data.provider} · {data.sending_domain || "unconfigured"}
          </div>
        </div>
        <span
          data-testid="email-health-domain-badge"
          style={{
            background: domainBg, color: domainTone, padding: "3px 10px",
            borderRadius: 999, fontSize: 11, fontWeight: 700, textTransform: "uppercase",
            letterSpacing: "0.05em",
          }}
        >
          {data.domain_status_label} · {data.domains_verified}/{data.domains_total}
        </span>
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 10 }}>
        <MiniStat label="Delivery" value={fmtPct(data.delivery_success_pct)} tone={successTone}
                  testid="ehw-delivery" />
        <MiniStat label="Bounce" value={fmtPct(data.bounce_rate_pct)}
                  tone={data.bounce_rate_pct <= 2 ? "#86EFAC" : "#FCD34D"}
                  testid="ehw-bounce" />
        <MiniStat label="Failure" value={fmtPct(data.failure_rate_pct)}
                  tone={data.failure_rate_pct <= 5 ? "#86EFAC" : "#FCA5A5"}
                  testid="ehw-failure" />
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, fontSize: 12, opacity: 0.92 }}>
        <div>
          <div style={{ opacity: 0.7 }}>Sent 24h</div>
          <div style={{ fontSize: 16, fontWeight: 700 }} data-testid="ehw-sent-24h">{data.sent_24h}</div>
        </div>
        <div>
          <div style={{ opacity: 0.7 }}>Failed 24h</div>
          <div style={{ fontSize: 16, fontWeight: 700 }} data-testid="ehw-failed-24h">{data.failed_24h}</div>
        </div>
        <div style={{ gridColumn: "1 / -1", display: "flex", alignItems: "center", gap: 6, fontSize: 11, opacity: 0.75 }}>
          <CheckCircle2 size={12} /> Last success: {fmtTs(data.last_success_at)}
        </div>
        {data.last_failure_at ? (
          <div style={{ gridColumn: "1 / -1", display: "flex", alignItems: "center", gap: 6, fontSize: 11, color: "#FCA5A5" }}>
            <AlertTriangle size={12} /> Last failure: {fmtTs(data.last_failure_at)}
          </div>
        ) : null}
      </div>

      <Link
        to="/admin/email-infrastructure"
        data-testid="ehw-open-dashboard"
        style={{
          display: "inline-flex", alignItems: "center", gap: 6,
          fontSize: 12, fontWeight: 600, color: "#F59E0B", textDecoration: "none",
          marginTop: "auto",
        }}
      >
        Open Email Infrastructure <ArrowRight size={12} />
      </Link>
    </div>
  );
}

function MiniStat({ label, value, tone, testid }) {
  return (
    <div style={{ background: "rgba(255,255,255,0.06)", borderRadius: 10, padding: 10 }} data-testid={testid}>
      <div style={{ fontSize: 10, letterSpacing: "0.14em", textTransform: "uppercase", opacity: 0.72 }}>{label}</div>
      <div style={{ fontSize: 20, fontWeight: 700, color: tone || "#fff" }}>{value}</div>
    </div>
  );
}
